// Apagar para a lixeira e a tela da lixeira, iguais para oscilografia e arquivo de eventos.
// A API tem as mesmas três rotas sob a base de cada tipo: `delete`, `trash`, `restore`.

import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, RotateCcw, Trash2 } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router";
import { api } from "../lib/api";
import { useAction } from "../lib/hooks";
import { formatDateTimeMs } from "../lib/units";
import { Alert, Button, EmptyState, Modal, PageHeader, Spinner, Table } from "./ui";

export interface TrashKind {
  /** Base das rotas na API (`/api/oscillography`, `/api/soe/files`). */
  api: string;
  /** Consultas a refazer depois de apagar ou restaurar. */
  invalidate: string[][];
  /** "oscilografia" / "arquivo de eventos". */
  one: string;
  /** "oscilografias" / "arquivos de eventos". */
  many: string;
  /** Página da lista, para o "voltar" da lixeira. */
  listPath: string;
  listLabel: string;
}

export function DeleteFilesDialog({
  kind,
  ids,
  files,
  onClose,
  onDone,
}: {
  kind: TrashKind;
  ids: number[];
  files: string[];
  onClose: () => void;
  onDone: () => void;
}) {
  const { run, busy, error } = useAction(kind.invalidate);
  return (
    <Modal
      open
      onClose={onClose}
      title={ids.length === 1 ? `Apagar ${kind.one}` : `Apagar ${ids.length} ${kind.many}`}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancelar
          </Button>
          <Button variant="danger" loading={busy} onClick={async () => (await run(() => api.post(`${kind.api}/delete`, { ids }))) && onDone()}>
            <Trash2 className="size-4" aria-hidden /> Apagar
          </Button>
        </>
      }
    >
      {error && <Alert kind="error">{error}</Alert>}
      <p className="text-sm">
        {ids.length === 1 ? (
          <>
            O arquivo <code className="font-mono">{files[0]}</code> some das telas para todos os usuários.
          </>
        ) : (
          <>Os arquivos selecionados somem das telas para todos os usuários.</>
        )}{" "}
        Fica na lixeira por 30 dias e pode ser restaurado; depois disso é removido de vez, com o arquivo bruto.
      </p>
      {ids.length > 1 && <p className="mt-2 max-h-32 overflow-y-auto font-mono text-xs text-muted">{files.join(", ")}</p>}
    </Modal>
  );
}

interface TrashRow {
  id: number;
  name: string;
  regulator_name: string;
  unit_name: string;
  site_name: string;
  start_ts: string | null;
  events: number | null;
  received_at: string;
  deleted_at: string;
  deleted_by_name: string | null;
  purge_at: string;
}

export function FileTrashPage({ kind, title }: { kind: TrashKind; title: string }) {
  const trash = useQuery({
    queryKey: [...kind.invalidate[0]!, "trash"],
    queryFn: () => api.get<{ trashDays: number; records: TrashRow[] }>(`${kind.api}/trash`),
  });
  const { run, busy, error } = useAction(kind.invalidate);
  const [restoring, setRestoring] = useState<number | null>(null);
  const fmt = (iso: string) => new Date(iso).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" });

  async function restore(id: number) {
    setRestoring(id);
    await run(() => api.post(`${kind.api}/restore`, { ids: [id] }));
    setRestoring(null);
  }

  return (
    <>
      <Link to={kind.listPath} className="mb-3 inline-flex items-center gap-1 text-sm text-muted hover:text-fg">
        <ArrowLeft className="size-4" aria-hidden /> {kind.listLabel}
      </Link>
      <PageHeader
        title={title}
        description={`Arquivos apagados das usinas que você administra. Ficam aqui por ${trash.data?.trashDays ?? 30} dias e depois são removidos de vez, com o arquivo bruto.`}
      />
      {error && (
        <div className="mb-4">
          <Alert kind="error">{error}</Alert>
        </div>
      )}
      {trash.isLoading ? (
        <Spinner />
      ) : !trash.data?.records.length ? (
        <EmptyState title="A lixeira está vazia" />
      ) : (
        <Table>
          <thead>
            <tr>
              <th>Arquivo</th>
              <th>Regulador</th>
              <th>Apagado</th>
              <th>Removido de vez em</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {trash.data.records.map((r) => (
              <tr key={r.id}>
                <td>
                  <p className="font-medium">{r.start_ts ? formatDateTimeMs(r.start_ts) : "sem hora válida"}</p>
                  <code className="font-mono text-xs text-muted">{r.name}</code>
                  {r.events !== null && <span className="ml-2 text-xs text-muted">{r.events} eventos</span>}
                </td>
                <td className="text-sm text-muted">
                  {r.site_name} › {r.unit_name} › {r.regulator_name}
                </td>
                <td className="text-sm whitespace-nowrap">
                  {fmt(r.deleted_at)}
                  {r.deleted_by_name && <p className="text-xs text-muted">por {r.deleted_by_name}</p>}
                </td>
                <td className="text-sm whitespace-nowrap text-muted">{fmt(r.purge_at)}</td>
                <td className="text-right">
                  <Button size="sm" variant="secondary" loading={busy && restoring === r.id} disabled={busy} onClick={() => restore(r.id)}>
                    <RotateCcw className="size-3.5" aria-hidden /> Restaurar
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </>
  );
}
